"use client";

import { useState, Fragment, useRef, useEffect, useCallback, ReactNode } from "react";
import dynamic from "next/dynamic";
import { useLocale } from "@/components/LocaleProvider";
import { projects } from "@/components/projects-data";

const ProjectsModal = dynamic(() => import("@/components/ProjectsModal"), {
  ssr: false,
});

// Renders outcome text with numbers (e.g. 25%, ~40%) in bold
function outcomeWithBoldNumbers(text: string): ReactNode {
  const parts = text.split(/(~?\d+%?)/g);
  return parts.map((part, i) =>
    /^~?\d+%?$/.test(part) ? (
      <strong key={i} className="text-foreground">{part}</strong>
    ) : (
      part
    )
  );
}

const CARD_BORDER_RADIUS = 8;

export default function Projects() {
  const { t } = useLocale();
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [cardDimensions, setCardDimensions] = useState<
    Record<number, { width: number; height: number }>
  >({});
  const [hoveredProjectIndex, setHoveredProjectIndex] = useState<number | null>(
    null
  );
  const cardRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const refs = cardRefs.current;
    const observers: ResizeObserver[] = [];
    projects.forEach((_, index) => {
      const el = refs[index];
      if (!el) return;
      const ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const el = entry.target as HTMLElement;
        const { width, height } = el.getBoundingClientRect();
        setCardDimensions((prev) => ({
          ...prev,
          [index]: {
            width: Math.max(1, width),
            height: Math.max(1, height),
          },
        }));
      });
      ro.observe(el);
      observers.push(ro);
    });
    return () => observers.forEach((ro) => ro.disconnect());
  }, []);

  const openProject = (index: number) => {
    setSelectedProject(index);
    document.body.style.overflow = "hidden";
  };

  const handleModalExited = useCallback(() => {
    setSelectedProject(null);
    document.body.style.overflow = "";
  }, []);

  return (
    <Fragment>
      <section
        id="projects"
        className="scroll-mt-12 pt-14 pb-14 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-[3em]"
      >
        <h2 className="inline-block text-sm font-bold font-montserrat uppercase bg-[#333333] text-white dark:bg-white dark:text-black px-3 py-1.5 mb-6">
          {t("sections.projects")}
        </h2>
        <p className="text-[15px] text-foreground/70 mb-12 leading-relaxed">
          {t("sections.projectsSubtitle")}
        </p>
        <div className="mb-12"></div>
        <div className="space-y-16">
          {projects.map((project, index) => {
            const dims = cardDimensions[index];
            const vbW = dims?.width ?? 100;
            const vbH = dims?.height ?? 100;
            const isHovered = hoveredProjectIndex === index;
            return (
              <div
                key={index}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                onClick={() => openProject(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openProject(index);
                  }
                }}
                onMouseEnter={() => setHoveredProjectIndex(index)}
                onMouseLeave={() => setHoveredProjectIndex(null)}
                onFocus={() => setHoveredProjectIndex(index)}
                onBlur={() => setHoveredProjectIndex(null)}
                tabIndex={0}
                role="button"
                className="group cursor-pointer relative rounded-lg p-4 sm:p-6 -m-4 sm:-m-6 border border-transparent transition-all duration-200 focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded card-header-shimmer-parent"
              >
                <svg
                  className="pointer-events-none absolute inset-0 h-full w-full overflow-visible rounded-lg text-[#e5e5e5] dark:text-[#ffffff1a]"
                  viewBox={`0 0 ${vbW} ${vbH}`}
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <rect
                    x={1}
                    y={1}
                    width={Math.max(0, vbW - 2)}
                    height={Math.max(0, vbH - 2)}
                    rx={CARD_BORDER_RADIUS}
                    ry={CARD_BORDER_RADIUS}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    pathLength={1}
                    className={`border-draw-path ${
                      isHovered ? "animate-border-draw" : ""
                    }`}
                  />
                </svg>
                <div className="relative z-[1]">
                  <h3
                    id={`project-card-title-${index}`}
                    className="text-base sm:text-lg font-bold text-foreground mb-2"
                  >
                    <span className="card-header-shimmer">{t(`projects.${index}.title`)}</span>
                  </h3>
                  <p className="text-sm sm:text-[15px] text-foreground/70 mb-2 leading-relaxed">
                    {t(`projects.${index}.description`)}
                  </p>
                  <div className="mb-3 sm:mb-4 space-y-1">
                    <p className="text-sm sm:text-[15px] text-foreground/70 leading-relaxed">
                    <strong className="text-foreground">
                      {t("sections.role")}:
                      </strong>{" "}
                      {t(`projects.${index}.role`)}
                    </p>
                    <p className="hidden sm:block text-[15px] text-foreground/70 leading-relaxed whitespace-pre-line">
                      <strong className="text-foreground">
                        {t("sections.outcome")}:
                      </strong>{" "}
                      {outcomeWithBoldNumbers(t(`projects.${index}.outcome`))}
                    </p>
                  </div>
                  <ul className="flex flex-wrap gap-1.5 sm:gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <li
                        key={techIndex}
                        className={`px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-full text-foreground/70 ${
                          techIndex > 2 ? "hidden sm:inline-flex" : ""
                        }`}
                      >
                        {tech}
                      </li>
                    ))}
                    {project.technologies.length > 3 && (
                      <li className="sm:hidden px-2.5 py-1 text-[11px] bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-full text-foreground/70">
                        +{project.technologies.length - 3}
                      </li>
                    )}
                  </ul>
                  <p className="mt-3 sm:hidden text-xs text-foreground/70 leading-relaxed">
                    {t("sections.viewDetailsFor")} {t(`projects.${index}.title`)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-0">
          <span className="inline-block text-base text-foreground/45">
            {/* Project Archive <span className="italic">[Coming Soon]</span> */}
          </span>
        </div>
      </section>

      {selectedProject !== null ? (
        <ProjectsModal projectIndex={selectedProject} onExited={handleModalExited} />
      ) : null}
    </Fragment>
  );
}
